import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import brand from './config/brand';
import usePushNotifications from './hooks/usePushNotifications';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import MobileNav from './components/MobileNav';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Appointments from './pages/Appointments';
import Invoices from './pages/Invoices';
import Quotes from './pages/Quotes';
import Services from './pages/Services';
import Settings from './pages/Settings';
import Mileage from './pages/Mileage';
import Trash from './pages/Trash';
import ResetPassword from './pages/ResetPassword';
import Users from './pages/Users';
import PublicInvoice from './pages/PublicInvoice';
import OrderForm from './pages/OrderForm';
import CallbackList from './pages/CallbackList';
import Financials from './pages/Financials';

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin') return <Navigate to="/" />;

  return children;
};

const FinancialsRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;
  if (user.role !== 'admin' && !user.can_view_financials) return <Navigate to="/" />;

  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  usePushNotifications();
  return (
    <div className="app">
      <Sidebar />
      <div className="app__content">
        <Header />
        <main className="app__main">
          {children}
        </main>
      </div>
      <MobileNav />
    </div>
  );
};

const App = () => {
  useEffect(() => {
    document.documentElement.style.setProperty('--color-gold', brand.primaryColor);
    document.title = `${brand.nameShort} CRM`;
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Dashboard />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Customers />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/customers/:id"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CustomerDetail />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Appointments />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Invoices />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Quotes />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Services />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Settings />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/mileage"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Mileage />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/trash"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <Trash />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <AppLayout>
                  <Users />
                </AppLayout>
              </AdminRoute>
            }
          />
          <Route
            path="/order-form"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <OrderForm />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/callback-list"
            element={
              <ProtectedRoute>
                <AppLayout>
                  <CallbackList />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/financials"
            element={
              <FinancialsRoute>
                <AppLayout>
                  <Financials />
                </AppLayout>
              </FinancialsRoute>
            }
          />
          <Route path="/invoice/:token" element={<PublicInvoice />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default App;
